import { Injectable, inject } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    combineLatest,
    delay,
    distinctUntilChanged,
    filter,
    map,
    mergeAll,
    of,
    shareReplay,
    startWith,
    switchMap,
    windowToggle,
    tap
} from 'rxjs';

import { BUSY_STATE_STREAMS, LAZY_BUSY_STATE_STREAMS_CHANGED } from './busy-state.provider';

@Injectable({
    providedIn: 'root'
})
export class LoadingStateService {
    private readonly mainBusyStreams = inject(BUSY_STATE_STREAMS, { optional: true }) ?? [];
    private readonly lazyBusyStreamChanged = inject(LAZY_BUSY_STATE_STREAMS_CHANGED);

    /**
     * Controls the state of the service. When `true`, the {@link isLoading$} feed will emit the combined busy state as it changes.
     * When `false`, the {@link isLoading$} will pause its emissions.
     *
     * @default true
     * @private
     */
    private readonly isEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    /**
     * Provides the combined busy state of different mechanisms in the platform.
     * The busy state of the mechanisms is hooked in and provided using the {@link BUSY_STATE_STREAMS} token.
     *
     * This observable emits `true` when at least one of the mechanism is busy, and `false` when all of them are idle.
     */
    public readonly isLoading$ = this.createLoadingStateFeed();
    /**
     * This observable emits the negated value for each value emitted by {@link isLoading$}.
     */
    public readonly isIdle$ = this.createIdleStateFeed();

    /**
     * Gets and sets the number of milliseconds to wait before emitting `false` values.
     *
     * **Why?**
     * In some cases, the loading state returns to idle only to quickly become busy again  (e.g. when routing finishs and http starts).
     * This might cause UI flickering. Delaying `false` values gives the {@link isLoading$} feed a chance to avoid those unneccesary
     * momentary emissions.
     *
     * For precise and instant emission, set this to `0`.
     *
     * @default 300
     */
    public delayIdleSignalMs = 300;

    private createLoadingStateFeed(): Observable<boolean> {
        const on = this.isEnabled$.pipe(filter((enabled) => enabled));
        const off = (): Observable<boolean> => this.isEnabled$.pipe(filter((enable) => !enable));

        // When lazy streams are registered/unregistered the last emitted values must be maintained for the `combineLatest` call to
        // emit for the first time. `shareReplay(1)` makes sure the main observables emit their latest value regardless of their type.
        const mainBusyStreams = this.mainBusyStreams.map((stream) => stream.pipe(shareReplay(1)));
        const lazyBusyStreams = this.lazyBusyStreamChanged.pipe(startWith<Observable<boolean>[]>([]));

        return lazyBusyStreams.pipe(
            map((lazyBusyStreams) => [...mainBusyStreams, ...lazyBusyStreams]),
            switchMap((busyStreams) => this.combineBusyStreamsToSingleSignal(busyStreams)),
            windowToggle(on, off),
            mergeAll(),
            shareReplay(1)
        );
    }

    private combineBusyStreamsToSingleSignal(busyStreams: Observable<boolean>[]): Observable<boolean> {
        return combineLatest(busyStreams).pipe(
            map((areBusy) => areBusy.some((busy) => busy)),
            distinctUntilChanged(),
            switchMap((busy) => (busy || this.delayIdleSignalMs === 0 ? of(busy) : of(busy).pipe(delay(this.delayIdleSignalMs))))
        );
    }

    private createIdleStateFeed(): Observable<boolean> {
        return this.isLoading$.pipe(map((isLoading) => !isLoading));
    }

    /**
     * Resumes emissions of the services' streams.
     *
     * @memberof LoadingStateService
     */
    public enable(): void {
        this.isEnabled$.next(true);
    }

    /**
     * Pauses emissions from the services' streams.
     *
     * @memberof LoadingStateService
     */
    public disable(): void {
        this.isEnabled$.next(false);
    }

    /**
     * `true` when emissions of the services' streams are enabled; otherwise `false`.
     *
     * @readonly
     * @type {boolean}
     * @memberof LoadingStateService
     */
    public get isEnabled(): boolean {
        return this.isEnabled$.value;
    }

    /**
     * `true` when emissions of the services' streams are paused; otherwise `false`.
     *
     * @readonly
     * @type {boolean}
     * @memberof LoadingStateService
     */
    public get isDisabled(): boolean {
        return !this.isEnabled;
    }
}
